import { PropsWithChildren, useMemo } from 'react';

import { Box, Center, Overlay } from '@mantine/core';
import { a, config, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';

import { Card } from '../../../../DataDisplay';
import classes from './SwipeableContainer.module.css';
import { useSwipeableContainerContext } from './SwipeableContainer.provider';

interface SwipeableContainerProps extends PropsWithChildren {
  containerHeight?: number;

  /**
   * How far from the bottom the card will be
   */
  defaultHeight?: number;
  /**
   * How far from the top of the container should be when fully open
   */
  openY?: number;
  /**
   * * How far from the top of the container should be when fully closed
   */
  closedY?: number;

  /**
   * The bounds for how far the container can go up or down
   */
  bounds?: {
    /**
     * How far from the top of the container can the card go
     */
    top: number;
    /**
     * How far from the bottom of the container can the card go
     */
    bottom: number;
  };

  /**
   * Controls the threshold for the card to open or close when the gesture is released
   */
  threshold?: {
    /**
     * What percent of the container will the card open when the gesture is released
     */
    upper?: number;
    /**
     * What percent of the container will the card close when the gesture is released
     */
    lower?: number;
  };
}

const Handle = (handleProps) => {
  return (
    <Box
      className={classes.handleContainer}
      renderRoot={(props) => <a.div {...props} {...handleProps} />}
    >
      <Center>
        <Box className={classes.handle} />
      </Center>
    </Box>
  );
};
const SwipeableContainer = ({
  children,
  defaultHeight = 250,
  containerHeight = 0,
  openY = 0,
  closedY = 0,
  bounds = {
    top: 0,
    bottom: 0,
  },
  threshold = {
    lower: 0,
    upper: 0,
  },
}: SwipeableContainerProps) => {
  /**
   * The open Y position from the bottom of the container
   */
  const _openY = useMemo(() => -Math.abs(containerHeight - openY), [containerHeight, openY]);
  const _bounds = useMemo(
    () => ({
      top: -Math.abs(containerHeight - bounds.top),
      bottom: -bounds.bottom,
    }),
    [bounds.bottom, bounds.top, containerHeight]
  );

  /**
   * The thresholds from Y to be considered open or closed
   */
  const _threshold = useMemo(() => {
    const lower = threshold.lower ? threshold.lower : 0;
    const upper = threshold.upper ? threshold.upper : 0;

    const upperPercent = (containerHeight / 100) * upper;
    const lowerPercent = (containerHeight / 100) * lower;
    return { lower: lowerPercent, upper: upperPercent };
  }, [containerHeight, threshold.lower, threshold.upper]);

  const [{ y }, api] = useSpring(() => ({
    y: -defaultHeight,
    config: { tension: 250, friction: 30 },
  }));

  const open = () => {
    // when cancel is true, it means that the user passed the upwards threshold
    api.start({
      // This is pixels up from the bottom of the screen
      y: _openY,
      immediate: false,
      config: config.stiff,
    });
  };
  const closeToDefault = (velocity = 0) => {
    api.start({
      y: -defaultHeight,
      immediate: false,
      config: { ...config.stiff, velocity },
    });
  };

  const bind = useDrag(
    ({
      last,
      velocity: [, velocityY],
      direction: [, directionY],
      offset: [, offsetY],
      swipe: [, swipeY],
      movement: [, y],
      cancel,
      canceled,
    }) => {
      // Should any up movement speed be considered an open?
      if (swipeY == -1) {
        // if the velocity is high enough, and the direction is upwards, we open the card
        open();
        //  ensure to cancel so that the users drag is canceled
        return cancel();
      } else if (swipeY == 1) {
        // if the direction is down, we cancel the drag and close the card
        closeToDefault(velocityY);
        return cancel();
      }

      if (last) {
        // If they are passed the upper threshold, we open the card fully
        if (offsetY < -_threshold.upper) {
          open();
          return cancel();
        }
        // If they are passed the lower bounds, we close the card fully
        const isLessThanStartingHeight = offsetY < -defaultHeight;
        if (offsetY > -_threshold.lower && isLessThanStartingHeight) {
          closeToDefault(velocityY);
          return cancel();
        }
      }

      api.start({ y: offsetY, immediate: true });
    },
    {
      from: () => [0, y.get()],
      bounds: { ..._bounds },
      filterTaps: true,
      rubberband: true,
      eventOptions: { passive: false },
    }
  );

  return (
    <Card
      component={a.div}
      className={classes.card}
      renderRoot={(props) => (
        <a.div
          {...props}
          style={{ ...props.style, transform: y.to((y) => `translate3D(0, ${y}px, 0)`) }}
        />
      )}
    >
      <Handle {...bind()} />

      <a.div
        className={classes.body}
        style={{ height: y.to((y) => Math.abs(y)), background: 'white' }}
      >
        {children}
        {/* Allows any padding provided to be swipeable  */}
        <Overlay color="transparent" style={{ zIndex: '-1' }} backgroundOpacity={0} {...bind()} />
      </a.div>
    </Card>
  );
};

const Wrapper = ({ children, ...props }: Omit<SwipeableContainerProps, 'height'>) => {
  const { containerHeight } = useSwipeableContainerContext();
  return (
    <SwipeableContainer containerHeight={containerHeight} {...props}>
      {children}
    </SwipeableContainer>
  );
};

Wrapper.Handle = Handle;

export { Wrapper as SwipeableContainer };
